// oxlint-disable no-explicit-any
import { defineQuery } from '@rocicorp/zero';
import * as z from 'zod';
import { AdministrationMode, CatalogType, PatientTherapySequenceStatus, PreparationType, PrescriptionSource, ProductionSource, QaResult, ReportTab, StockMovementSource, StockMovementType, } from '../database/schema.js';
import { applyCursor } from '../util/apply-cursor.js';
import { assertIsAuthenticated } from '../utils.js';
import { zql } from '../zero-schema.gen.js';
import { getSequenceMedicines } from './patients-therapies.queries.js';
import { Cursor, Sort } from './util.js';
/** Date bases the period can run on. Only bases backed by a sequence column are offered. */
export const PrescriptionReportDateBasis = z.enum([
    'applicationDate',
    'productionDay',
    'prescribedOn',
    'pharmacistValidatedOn',
    'producedOn',
    'releasedOn',
    'administeredOn',
]);
// Production day has no column of its own: it is the day the picking list (order) was created, see applyPeriod.
const DATE_BASIS_COLUMN = {
    applicationDate: 'therapyDate',
    prescribedOn: 'createdAt',
    pharmacistValidatedOn: 'pharmacistValidatedAt',
    producedOn: 'producedAt',
    releasedOn: 'releasedAt',
    administeredOn: 'administeredAt',
};
/** The Period filter on the chosen date basis (CY-356). */
function applyPeriod(q, filters) {
    const { periodFrom, periodTo } = filters;
    if (periodFrom == null && periodTo == null)
        return q;
    const basis = filters.dateBasis ?? 'applicationDate';
    if (basis === 'productionDay') {
        return q.whereExists('order', order => {
            if (periodFrom != null)
                order = order.where('createdAt', '>=', periodFrom);
            if (periodTo != null)
                order = order.where('createdAt', '<=', periodTo);
            return order;
        });
    }
    const column = DATE_BASIS_COLUMN[basis];
    if (periodFrom != null)
        q = q.where(column, '>=', periodFrom);
    if (periodTo != null)
        q = q.where(column, '<=', periodTo);
    return q;
}
/**
 * Prescriptions-tab filters, one optional field per filter in the Coda table. A field that is not
 * set does not narrow anything; values inside one filter are OR, filters between them are AND.
 */
export const PrescriptionReportFilters = z.object({
    dateBasis: PrescriptionReportDateBasis.optional(),
    periodFrom: z.number().optional(),
    periodTo: z.number().optional(),
    status: z.array(PatientTherapySequenceStatus).optional(),
    prescriptionTypes: z.array(CatalogType).optional(),
    origin: z.enum(['protocol', 'individual', 'both']).optional(),
    protocolIds: z.array(z.uuidv7()).optional(),
    priorApproval: z.enum(['not_required', 'pending', 'approved']).optional(),
    hasPrescriptionNote: z.boolean().optional(),
    hasPharmacyNote: z.boolean().optional(),
    patientIds: z.array(z.uuidv7()).optional(),
    hospitalIdSearch: z.string().optional(),
    sectionIds: z.array(z.uuidv7()).optional(),
    wardIds: z.array(z.uuidv7()).optional(),
    insuranceCompanyIds: z.array(z.uuidv7()).optional(),
    diagnosisIds: z.array(z.uuidv7()).optional(),
    activeSubstanceIds: z.array(z.uuidv7()).optional(),
    prescribedNameIds: z.array(z.uuidv7()).optional(),
    applicationFormIds: z.array(z.uuidv7()).optional(),
    carrierSolutionIds: z.array(z.uuidv7()).optional(),
    prescribedByIds: z.array(z.uuidv7()).optional(),
    responsibleDoctorIds: z.array(z.uuidv7()).optional(),
    doctorValidatedByIds: z.array(z.uuidv7()).optional(),
    pharmacistValidatedByIds: z.array(z.uuidv7()).optional(),
    administeredByIds: z.array(z.uuidv7()).optional(),
    tradeNameIds: z.array(z.uuidv7()).optional(),
    batchNumberSearch: z.string().optional(),
    preparationTypes: z.array(PreparationType).optional(),
    productionLocationIds: z.array(z.uuidv7()).optional(),
    prescriptionSource: PrescriptionSource.optional(),
    producedByIds: z.array(z.uuidv7()).optional(),
    productionSource: ProductionSource.optional(),
    restVialCreated: z.boolean().optional(),
    qaResult: QaResult.optional(),
    administrationModes: z.array(AdministrationMode).optional(),
});
/** Sorting uses the same cursor contract as the activity list so the two lists share paging behaviour. */
export const ListPrescriptionReportInput = z.object({
    filters: PrescriptionReportFilters.optional(),
    limit: z.number().optional(),
    cursor: Cursor.extend({
        createdAt: z.number().nullable(),
        therapyDate: z.number().nullable(),
        time: z.string().optional(),
        internalId: z.string().nullable(),
        status: z.string().nullable(),
        cycle: z.number().nullable(),
        day: z.number().nullable(),
        medicineNames: z.string().nullable(),
        pharmacistValidatedAt: z.number().nullable(),
        doctorValidatedAt: z.number().nullable(),
        administeredAt: z.number().nullable(),
    }).optional(),
    sort: Sort.extend({
        field: z.string().default('therapyDate'),
    }),
});
/** Stock movements-tab filters, one optional field per filter in the Coda table. */
export const StockMovementReportFilters = z.object({
    periodFrom: z.number().optional(),
    periodTo: z.number().optional(),
    movementTypes: z.array(StockMovementType).optional(),
    source: StockMovementSource.optional(),
    userIds: z.array(z.uuidv7()).optional(),
    stockLocationIds: z.array(z.uuidv7()).optional(),
    tradeNameIds: z.array(z.uuidv7()).optional(),
    activeSubstanceIds: z.array(z.uuidv7()).optional(),
    categoryIds: z.array(z.uuidv7()).optional(),
    subcategoryIds: z.array(z.uuidv7()).optional(),
    manufacturerIds: z.array(z.uuidv7()).optional(),
    productCodeSearch: z.string().optional(),
    batchNumberSearch: z.string().optional(),
    supplierCompanyIds: z.array(z.uuidv7()).optional(),
    expiryBefore: z.number().optional(),
    prescriptionNumberSearch: z.string().optional(),
    patientIds: z.array(z.uuidv7()).optional(),
    sectionIds: z.array(z.uuidv7()).optional(),
    wardIds: z.array(z.uuidv7()).optional(),
    responsibleDoctorIds: z.array(z.uuidv7()).optional(),
    protocolIds: z.array(z.uuidv7()).optional(),
});
export const ListStockMovementsInput = z.object({
    filters: StockMovementReportFilters.optional(),
    limit: z.number().optional(),
    cursor: Cursor.extend({
        performedAt: z.number().nullable(),
    }).optional(),
    sort: Sort.extend({
        field: z.string().default('performedAt'),
    }),
});
/** Shared Reports views (CY-85): one list per tab, in their shared order. */
export const ListReportViewsInput = z.object({
    tab: ReportTab,
});
function namePattern(value) {
    return `%${value.trim()}%`;
}
/** Adds a note-presence filter, treating both NULL and the empty string as "no note". */
function withNotePresence(q, column, has) {
    if (has)
        return q.where(({ and, cmp }) => and(cmp(column, 'IS NOT', null), cmp(column, '!=', '')));
    return q.where(({ cmp, or }) => or(cmp(column, 'IS', null), cmp(column, '=', '')));
}
/**
 * Applies the Prescriptions filters to the sequence query. Multi-value filters use IN (OR inside
 * the filter); each filter narrows the result further (AND between filters).
 */
function applyPrescriptionFilters(q, filters) {
    q = applyPeriod(q, filters);
    if (filters.status?.length)
        q = q.where('status', 'IN', filters.status);
    if (filters.prescriptionTypes?.length) {
        const types = filters.prescriptionTypes;
        q = q.whereExists('medicine', medicine => medicine.whereExists('activeSubstance', drug => drug.where('catalogType', 'IN', types)));
    }
    if (filters.origin === 'protocol')
        q = q.where('protocolId', 'IS NOT', null);
    else if (filters.origin === 'individual')
        q = q.where('protocolId', 'IS', null);
    if (filters.protocolIds?.length)
        q = q.where('protocolId', 'IN', filters.protocolIds);
    if (filters.priorApproval === 'pending') {
        q = q.where('status', '=', PatientTherapySequenceStatus.enum.pending_approval);
    }
    else if (filters.priorApproval === 'approved') {
        q = q
            .where('status', 'NOT IN', [
            PatientTherapySequenceStatus.enum.pending_approval,
            PatientTherapySequenceStatus.enum.budget_not_approved,
        ])
            .whereExists('medicine', medicine => medicine.whereExists('activeSubstance', drug => drug.whereExists('activeSubstanceDetails', details => details.where('additionalApprovalRequired', '=', true))));
    }
    else if (filters.priorApproval === 'not_required') {
        q = q.whereExists('medicine', medicine => medicine.whereExists('activeSubstance', drug => drug.whereExists('activeSubstanceDetails', details => details.where('additionalApprovalRequired', '=', false))));
    }
    if (filters.hasPrescriptionNote != null)
        q = withNotePresence(q, 'therapyNote', filters.hasPrescriptionNote);
    if (filters.hasPharmacyNote != null)
        q = withNotePresence(q, 'pharmacyNote', filters.hasPharmacyNote);
    if (filters.patientIds?.length)
        q = q.where('patientId', 'IN', filters.patientIds);
    if (filters.hospitalIdSearch?.trim()) {
        const pattern = namePattern(filters.hospitalIdSearch);
        q = q.whereExists('patient', patient => patient.where('hospitalPatientId', 'ILIKE', pattern));
    }
    if (filters.sectionIds?.length || filters.wardIds?.length) {
        const { sectionIds, wardIds } = filters;
        q = q.whereExists('patient', patient => patient.whereExists('encounters', encounters => {
            let latest = encounters.where('deletedAt', 'IS', null).orderBy('createdAt', 'desc').limit(1);
            if (sectionIds?.length)
                latest = latest.where('sectionId', 'IN', sectionIds);
            if (wardIds?.length)
                latest = latest.where('wardId', 'IN', wardIds);
            return latest;
        }));
    }
    if (filters.insuranceCompanyIds?.length) {
        const ids = filters.insuranceCompanyIds;
        q = q.whereExists('patient', patient => patient.whereExists('insurance', insurance => insurance.where('companyId', 'IN', ids)));
    }
    if (filters.diagnosisIds?.length) {
        const ids = filters.diagnosisIds;
        q = q.where(({ or, exists }) => or(exists('protocol', protocol => protocol.where('diagnosisId', 'IN', ids)), exists('patient', patient => patient.whereExists('characteristics', characteristic => characteristic.where('diagnosisId', 'IN', ids)))));
    }
    if (filters.activeSubstanceIds?.length) {
        const ids = filters.activeSubstanceIds;
        q = q.whereExists('medicine', medicine => medicine.where('activeSubstanceId', 'IN', ids));
    }
    if (filters.prescribedNameIds?.length) {
        const ids = filters.prescribedNameIds;
        q = q.whereExists('medicine', medicine => medicine.where('prescribedNameId', 'IN', ids));
    }
    if (filters.applicationFormIds?.length)
        q = q.where('applicationFormId', 'IN', filters.applicationFormIds);
    if (filters.carrierSolutionIds?.length)
        q = q.where('carrierSolutionId', 'IN', filters.carrierSolutionIds);
    if (filters.prescribedByIds?.length)
        q = q.where('createdBy', 'IN', filters.prescribedByIds);
    if (filters.responsibleDoctorIds?.length) {
        const ids = filters.responsibleDoctorIds;
        q = q.whereExists('patient', patient => patient.where('responsibleDoctorId', 'IN', ids));
    }
    if (filters.doctorValidatedByIds?.length)
        q = q.where('doctorValidatedBy', 'IN', filters.doctorValidatedByIds);
    if (filters.pharmacistValidatedByIds?.length)
        q = q.where('pharmacistValidatedBy', 'IN', filters.pharmacistValidatedByIds);
    if (filters.administeredByIds?.length)
        q = q.where('administeredBy', 'IN', filters.administeredByIds);
    // filters match the lots this prescription used, not every batch of the picking list (CY-382)
    if (filters.tradeNameIds?.length) {
        const ids = filters.tradeNameIds;
        q = q.whereExists('batches', orderBatch => orderBatch.whereExists('batch', batch => batch.where('tradeNameId', 'IN', ids)));
    }
    if (filters.batchNumberSearch?.trim()) {
        const pattern = namePattern(filters.batchNumberSearch);
        q = q.whereExists('batches', orderBatch => orderBatch.whereExists('batch', batch => batch.where('lotNumber', 'ILIKE', pattern)));
    }
    if (filters.preparationTypes?.length)
        q = q.where('preparationType', 'IN', filters.preparationTypes);
    if (filters.prescriptionSource)
        q = q.where('prescriptionSource', '=', filters.prescriptionSource);
    if (filters.producedByIds?.length)
        q = q.where('producedBy', 'IN', filters.producedByIds);
    if (filters.productionSource)
        q = q.where('productionSource', '=', filters.productionSource);
    if (filters.restVialCreated != null)
        q = q.where('restVialCreated', '=', filters.restVialCreated);
    if (filters.qaResult)
        q = q.where('qaResult', '=', filters.qaResult);
    if (filters.administrationModes?.length) {
        q = q.where('administrationMode', 'IN', filters.administrationModes);
    }
    if (filters.productionLocationIds?.length) {
        const ids = filters.productionLocationIds;
        q = q.whereExists('order', order => order.where('productionLocationId', 'IN', ids));
    }
    return q;
}
/**
 * Everything one Prescriptions-tab row needs, hung off the prescription (the sequence):
 * patient + identifiers + current encounter, protocol/diagnosis, medicines with their dose and
 * substance, both validations, the production order with its batches, and the cleanroom readings.
 */
function populatePrescriptionReport(q) {
    return (q
        .related('patient', patient => patient
        .related('responsibleDoctor')
        .related('identifiers', identifiers => identifiers.where('deletedAt', 'IS', null))
        .related('insurance', insurance => insurance.related('insuranceCompany'))
        .related('encounters', encounters => encounters
        .where('deletedAt', 'IS', null)
        .orderBy('createdAt', 'desc')
        .limit(1)
        .related('ward')
        .related('section')
        .related('customer'))
        .related('characteristics', characteristics => characteristics.where('deletedAt', 'IS', null).orderBy('createdAt', 'desc').limit(1).related('diagnosis'))
        .related('gfrs', gfrs => gfrs.where('deletedAt', 'IS', null).orderBy('labTime', 'desc').orderBy('createdAt', 'desc').limit(1)))
        .related('protocol', protocol => protocol.related('diagnosis'))
        .related('created')
        .related('applicationForm')
        .related('carrierSolution')
        .related('medicine', getSequenceMedicines)
        .related('doctorValidated')
        .related('pharmacistValidated')
        .related('administeredByUser')
        .related('order', order => order.related('productionLocation'))
        // only the lots this prescription used, not every batch of the picking list (CY-382)
        .related('batches', batches => batches.related('batch', batch => batch.where('deletedAt', 'IS', null).related('tradeName').related('stockLocation').related('supplierCompany')))
        .related('productionDetails', details => details.where('deletedAt', 'IS', null).orderBy('recordedAt', 'asc')));
}
/** Applies the Stock movements filters; multi-value filters are OR inside, filters AND between. */
function applyStockMovementFilters(q, filters) {
    if (filters.periodFrom != null)
        q = q.where('performedAt', '>=', filters.periodFrom);
    if (filters.periodTo != null)
        q = q.where('performedAt', '<=', filters.periodTo);
    if (filters.movementTypes?.length)
        q = q.where('type', 'IN', filters.movementTypes);
    if (filters.source)
        q = q.where('source', '=', filters.source);
    if (filters.userIds?.length)
        q = q.where('createdBy', 'IN', filters.userIds);
    if (filters.stockLocationIds?.length)
        q = q.where('stockLocationId', 'IN', filters.stockLocationIds);
    if (filters.tradeNameIds?.length) {
        const ids = filters.tradeNameIds;
        q = q.whereExists('batch', batch => batch.where('tradeNameId', 'IN', ids));
    }
    if (filters.activeSubstanceIds?.length) {
        const ids = filters.activeSubstanceIds;
        q = q.whereExists('batch', batch => batch.whereExists('tradeName', tradeName => tradeName.whereExists('prescribedName', prescribedName => prescribedName.where('drugCatalogItemId', 'IN', ids))));
    }
    if (filters.categoryIds?.length || filters.subcategoryIds?.length) {
        const ids = [...(filters.categoryIds ?? []), ...(filters.subcategoryIds ?? [])];
        q = q.whereExists('batch', batch => batch.whereExists('tradeName', tradeName => tradeName.whereExists('prescribedName', prescribedName => prescribedName.whereExists('drugCatalogItem', drug => drug.whereExists('categories', category => category.where('drugCategoryId', 'IN', ids))))));
    }
    if (filters.manufacturerIds?.length) {
        const ids = filters.manufacturerIds;
        q = q.whereExists('batch', batch => batch.whereExists('tradeName', tradeName => tradeName.where('manufacturerId', 'IN', ids)));
    }
    if (filters.productCodeSearch?.trim()) {
        const pattern = namePattern(filters.productCodeSearch);
        q = q.whereExists('batch', batch => batch.whereExists('tradeName', tradeName => tradeName.where('barcodeGtin', 'ILIKE', pattern)));
    }
    if (filters.batchNumberSearch?.trim()) {
        const pattern = namePattern(filters.batchNumberSearch);
        q = q.whereExists('batch', batch => batch.where('lotNumber', 'ILIKE', pattern));
    }
    if (filters.supplierCompanyIds?.length) {
        const ids = filters.supplierCompanyIds;
        q = q.whereExists('batch', batch => batch.where('supplierCompanyId', 'IN', ids));
    }
    if (filters.expiryBefore != null) {
        q = q.whereExists('batch', batch => batch.where('expiryDate', '<=', filters.expiryBefore));
    }
    if (filters.patientIds?.length) {
        const ids = filters.patientIds;
        q = q.whereExists('patientTherapySequence', sequence => sequence.where('patientId', 'IN', ids));
    }
    if (filters.protocolIds?.length) {
        const ids = filters.protocolIds;
        q = q.whereExists('patientTherapySequence', sequence => sequence.where('protocolId', 'IN', ids));
    }
    if (filters.prescriptionNumberSearch?.trim()) {
        const pattern = namePattern(filters.prescriptionNumberSearch);
        q = q.whereExists('patientTherapySequence', sequence => sequence.where('internalId', 'ILIKE', pattern));
    }
    if (filters.responsibleDoctorIds?.length) {
        const ids = filters.responsibleDoctorIds;
        q = q.whereExists('patientTherapySequence', sequence => sequence.whereExists('patient', patient => patient.where('responsibleDoctorId', 'IN', ids)));
    }
    if (filters.sectionIds?.length || filters.wardIds?.length) {
        const { sectionIds, wardIds } = filters;
        q = q.whereExists('patientTherapySequence', sequence => sequence.whereExists('patient', patient => patient.whereExists('encounters', encounters => {
            let latest = encounters.where('deletedAt', 'IS', null).orderBy('createdAt', 'desc').limit(1);
            if (sectionIds?.length)
                latest = latest.where('sectionId', 'IN', sectionIds);
            if (wardIds?.length)
                latest = latest.where('wardId', 'IN', wardIds);
            return latest;
        })));
    }
    return q;
}
/** Everything one Stock movements-tab row needs, hung off the movement. */
function populateStockMovement(q) {
    return q
        .related('created')
        .related('quantityUnit')
        .related('stockLocation')
        .related('batch', batch => batch
        .related('tradeName', tradeName => tradeName
        .related('strengthUnit')
        .related('manufacturer')
        .related('prescribedName', prescribedName => prescribedName.related('drugCatalogItem', drug => drug.related('categories', category => category.related('drugCategory', drugCategory => drugCategory.related('parent'))))))
        .related('consumable')
        .related('supplierCompany')
        .related('stockLocation'))
        .related('restVial')
        .related('return')
        .related('discard')
        .related('patientTherapySequence', sequence => sequence
        .related('protocol')
        .related('patient', patient => patient
        .related('responsibleDoctor')
        .related('encounters', encounters => encounters
        .where('deletedAt', 'IS', null)
        .orderBy('createdAt', 'desc')
        .limit(1)
        .related('ward')
        .related('section')
        .related('customer'))));
}
export const reportsQueries = {
    listPrescriptions: defineQuery(ListPrescriptionReportInput, ({ args, ctx }) => {
        assertIsAuthenticated(ctx);
        // notes are not prescriptions and must never appear as a report row
        let q = zql.patientTherapySequence
            .where('deletedAt', 'IS', null)
            .where('isNote', '=', false);
        if (args.filters)
            q = applyPrescriptionFilters(q, args.filters);
        q = applyCursor(q, { cursor: args.cursor, limit: args.limit, sort: args.sort });
        q = populatePrescriptionReport(q);
        return q;
    }),
    listStockMovements: defineQuery(ListStockMovementsInput, ({ args, ctx }) => {
        assertIsAuthenticated(ctx);
        let q = zql.stockMovements.where('deletedAt', 'IS', null);
        if (args.filters)
            q = applyStockMovementFilters(q, args.filters);
        q = applyCursor(q, { cursor: args.cursor, limit: args.limit, sort: args.sort });
        q = populateStockMovement(q);
        return q;
    }),
    getStockMovementById: defineQuery(z.string(), ({ args, ctx }) => {
        assertIsAuthenticated(ctx);
        return populateStockMovement(zql.stockMovements.where('id', '=', args)).one();
    }),
    listReportViews: defineQuery(ListReportViewsInput, ({ args, ctx }) => {
        assertIsAuthenticated(ctx);
        return zql.reportViews.where('tab', '=', args.tab).where('deletedAt', 'IS', null).orderBy('position', 'asc');
    }),
};
//# sourceMappingURL=reports.queries.js.map